<template>
  <div
    :class="[
      $style.base,
      { [$style.smallScreen]: $screen === 's' || $screen === 'm' }
    ]"
    v-test="'tw-dashboard-pages'"
  >
    <Alerts
      @kyc="emit('kyc')"
      @learnMore="emit('learnMore')"
    />
    <CommissionChangeBanner />
    <div :class="$style.container">
      <div :class="$style.containerLeft">
        <LeftNav
          v-model:section="section"
          :setupDone="enableSection"
        />
      </div>
      <Transition
        name="section-fade"
        mode="out-in"
      >
        <div
          v-if="section === 'profile'"
          :class="$style.containerRight"
        >
          <Profile
            :treatwellVenue="treatwellVenue"
            :enableSection="enableSection"
            :updated="updated"
          />
        </div>
        <div
          v-else-if="section === 'reviews'"
          :class="$style.containerRight"
        >
          <Reviews
            :treatwellVenue="treatwellVenue"
            :enableSection="enableSection"
          />
        </div>
        <div
          v-else
          :class="$style.containerRight"
        >
          <Overview
            :treatwellVenue="treatwellVenue"
            :contractDownloadUrl="contractDownloadUrl"
            @kyc="emit('kyc')"
            @subscribe="emit('subscribe')"
            @cancel="emit('cancel')"
            @open="section = $event"
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { watch } from 'vue';
import type { TreatwellVenue } from '@/types';
import Alerts from '../Alerts.vue';
import CommissionChangeBanner from '../CommissionChangeBanner.vue';
import LeftNav from './LeftNav.vue';
import Overview from './Overview.vue';
import Profile from './Profile.vue';
import Reviews from './Reviews.vue';
import type { DashboardSection, UpdatedTasks } from './types';

// The marketplace dashboard as pages, like the Salonized Pay dashboard: the required setup
// first, then the profile and the reviews
const props = defineProps<{
  treatwellVenue: Partial<TreatwellVenue> | null;
  contractDownloadUrl?: string | null;
  enableSection: boolean;
  updated: UpdatedTasks;
}>();

const emit = defineEmits<{
  kyc: [];
  learnMore: [];
  subscribe: [];
  cancel: [];
}>();

// Kept by the parent: the page content remounts while ModuleBase reloads (e.g. after a save)
const section = defineModel<DashboardSection>('section', { required: true });

// Back to the setup if the profile and reviews close again
watch(
  () => props.enableSection,
  (enabled) => {
    if (!enabled) {
      section.value = 'dashboard';
    }
  },
);
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  gap: $spacing;
  width: 100%;
  max-width: 910px;
  margin: 0 auto;
  padding-bottom: $spacing;
}

.container {
  display: flex;
  gap: $spacing;

  .base:not(.smallScreen) & {
    align-items: flex-start;
  }

  .base.smallScreen & {
    flex-direction: column;
  }
}

.containerLeft {
  .base:not(.smallScreen) & {
    width: 234px;
    flex-shrink: 0;
  }
}

.containerRight {
  width: 100%;
  // Wide content scrolls inside its card instead of pushing the page wider
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing;
}
</style>

<style lang="scss">
.section-fade-enter-active,
.section-fade-leave-active {
  transition: opacity 0.15s ease;
}

.section-fade-enter-from,
.section-fade-leave-to {
  opacity: 0;
}
</style>
